import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import Navbar from './Navbar';
import { TiDelete } from "react-icons/ti";
import './Historial.css';
import { checkAndUpdateEmployeeData } from './employeeUpdates';
import { calculateDaysTaken } from '../utils/daysTakenCalculation';

function Historial() {
    const [email, setEmail] = useState('@wsp.com.mx');
    const [currentView, setCurrentView] = useState('email');
    const [employee, setEmployee] = useState(null);
    const [vacationDays, setVacationDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userRole, setUserRole] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [calculatedDaysTaken, setCalculatedDaysTaken] = useState(0);
    const [overtimeData, setOvertimeData] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);

    // Get the user's info when component mounts
    useEffect(() => {
        async function getUserInfo() {
            try {
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    const userEmail = user.email;
                    setUserEmail(userEmail);
                    
                    const { data, error } = await supabase
                        .from('users')
                        .select('role')
                        .eq('wsp_email', userEmail)
                        .single();

                    if (error) throw error;
                    const userRole = data.role;
                    setUserRole(userRole);
                    
                    // If user is an employee, automatically load their data
                    if (userRole === 'Empleado') {
                        setEmail(userEmail);
                        loadEmployeeData(userEmail, userRole, userEmail);
                    } else {
                        setLoading(false);
                    }
                } else {
                    setLoading(false);
                }
            } catch (error) {
                console.error('Error fetching user info:', error.message);
                setError('Error al obtener información del usuario. Por favor, intente nuevamente.');
                setLoading(false);
            }
        }
        
        getUserInfo();
    }, []);

    // Extract years from vacation days and set available years
    useEffect(() => {
        if (vacationDays.length > 0) {
            const years = vacationDays.map(day => {
                const date = new Date(day.vacation_date);
                return date.getFullYear();
            });
            
            // Get unique years and sort in descending order
            const uniqueYears = [...new Set(years)].sort((a, b) => b - a);
            setAvailableYears(uniqueYears);
            
            // If current year is not in the list, default to the most recent year
            if (!uniqueYears.includes(selectedYear)) {
                setSelectedYear(uniqueYears[0]);
            }
        }
    }, [vacationDays]);

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    };

    // Function to check if the user has permission to view the employee
    const checkUserPermission = (role, employeeArea, currentUserEmail, employeeEmail) => {
        // Users can always view their own data
        if (currentUserEmail === employeeEmail) {
            return true;
        }
        
        // Admin and RH can access all employees
        if (role === 'admin' || role === 'RH') {
            return true;
        }
        
        // CEO can only access certain areas
        if (role === 'CEO') {
            return ['Administrativo / Dirección', 'Comercial', 'Ventas'].includes(employeeArea);
        }
        
        // Other roles can only access employees in their own area
        if (['Fábrica ABAP', 'Staffing', 'PMO-IT', 'Finanzas'].includes(role)) {
            return employeeArea === role;
        }
        
        // Default deny
        return false;
    };

    // Function to delete old vacation records
    const cleanupOldVacationRecords = async (vacationData) => {
        try {
            const currentDate = new Date();
            const fourYearsAgo = new Date(currentDate.setFullYear(currentDate.getFullYear() - 4));
            
            // Filter out records older than 4 years
            const oldRecords = vacationData.filter(day => {
                const vacationDate = new Date(day.vacation_date);
                return vacationDate < fourYearsAgo;
            });
            
            if (oldRecords.length > 0) {
                console.log(`Deleting ${oldRecords.length} vacation records older than 4 years`);
                
                // Delete old records from database
                const oldRecordIds = oldRecords.map(record => record.id);
                
                const { error } = await supabase
                    .from('vacation_days')
                    .delete()
                    .in('id', oldRecordIds);
                    
                if (error) throw error;
                
                // Return the remaining valid records
                return vacationData.filter(day => !oldRecordIds.includes(day.id));
            }
            
            return vacationData;
        } catch (error) {
            console.error('Error cleaning up old vacation records:', error);
            // Return original data if cleanup fails
            return vacationData;
        }
    };

    // Function to load employee data
    const loadEmployeeData = async (emailToLoad, currentUserRole = userRole, currentUserEmail = userEmail) => {
        setLoading(true);
        setError('');

        try {
            // First, get the employee data
            const { data: employeeData, error: employeeError } = await supabase
                .from('employees')
                .select('*')
                .eq('wsp_email', emailToLoad.toLowerCase())
                .single();

            if (employeeError || !employeeData) {
                if (currentUserRole === 'Empleado') {
                    setError('No se encontró su registro de empleado en el sistema. Por favor contacte a Recursos Humanos.');
                } else {
                    setError('El correo WSP proporcionado no es valido.\nRevise nuevamente que este bien escrito sin espacios sobrantes o un punto al final.\nSi el problema persiste, contacte al administrador de la página');
                }
                setLoading(false);
                return;
            }

            // Check if the user has permission to view this employee's data
            const hasPermission = checkUserPermission(
                currentUserRole, 
                employeeData.area,
                currentUserEmail, 
                emailToLoad
            );
            
            if (!hasPermission) {
                setError('Usted no tiene permiso para consultar el historial de este empleado.');
                setLoading(false);
                return;
            }

            // Run the employee through the update algorithm
            const result = await checkAndUpdateEmployeeData([employeeData]);

            // Check if the update was successful and we have updated employee data
            if (!result.success || !result.updatedEmployees || result.updatedEmployees.length === 0) {
                setError('Error al actualizar los datos del empleado.');
                setLoading(false);
                return;
            }

            const updatedEmployee = result.updatedEmployees[0];

            // Fetch vacation days for the employee
            const { data: vacationData, error: vacationError } = await supabase
                .from('vacation_days')
                .select('*')
                .eq('employee_id', employeeData.id)
                .order('vacation_date', { ascending: false });

            if (vacationError) {
                throw vacationError;
            }

            // Clean up old vacation records (older than 4 years)
            const cleanedVacationData = await cleanupOldVacationRecords(vacationData || []);

            // Calculate days taken using the new function
            const calculatedDays = await calculateDaysTaken(
                updatedEmployee.renovation_day,
                updatedEmployee.id
            );

            // Fetch overtime data
            const { data: overtimeData, error: overtimeError } = await supabase
                .from('overtime_vacation_days')
                .select('*')
                .eq('employee_id', employeeData.id)
                .single();

            if (!overtimeError || overtimeError.code === 'PGRST116') {
                setOvertimeData(overtimeData);
            }

            // Set current year as default selected year
            setSelectedYear(new Date().getFullYear());
            
            setCalculatedDaysTaken(calculatedDays);
            setEmployee(employeeData);
            setVacationDays(cleanedVacationData);
            setCurrentView('history');
        } catch (error) {
            console.error('Error:', error);
            setError('Error al verificar el empleado. Por favor, intente nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => {
        loadEmployeeData(email);
    };

    const handleDelete = async (id) => {
        try {
            // First, get the vacation day details to check if it's a half day
            const { data: vacationDay, error: vacationError } = await supabase
                .from('vacation_days')
                .select('has_half_day')
                .eq('id', id)
                .single();

            if (vacationError) throw vacationError;

            // Determine adjustment amount based on day type (half or full)
            const adjustmentAmount = vacationDay.has_half_day ? 0.5 : 1;

            // Delete the vacation day
            const { error } = await supabase
                .from('vacation_days')
                .delete()
                .eq('id', id);

            if (error) throw error;

            // Update the local state to remove the deleted item
            setVacationDays(prevDays => prevDays.filter(day => day.id !== id));

            // Update the employee's days_taken and remaining_days
            const { data: employeeData, error: employeeError } = await supabase
                .from('employees')
                .select('days_taken, remaining_days')
                .eq('id', employee.id)
                .single();

            if (employeeError) throw employeeError;

            // Apply the correct adjustment based on day type
            const { data: updatedEmployee, error: updateError } = await supabase
                .from('employees')
                .update({
                    days_taken: Math.max(0, (employeeData.days_taken || 0) - adjustmentAmount),
                    remaining_days: (employeeData.remaining_days || 0) + adjustmentAmount
                })
                .eq('id', employee.id)
                .select()
                .single();

            if (updateError) throw updateError;

            // Update the local employee state with new values
            setEmployee(updatedEmployee);

            // Recalculate days taken using the calculation function
            const recalculatedDays = await calculateDaysTaken(
                employee.renovation_day,
                employee.id
            );
            setCalculatedDaysTaken(recalculatedDays);

        } catch (error) {
            console.error('Error deleting vacation day:', error);
            setError('Error al eliminar el día de vacaciones. Por favor, intente nuevamente.');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        // Add one day to compensate for timezone
        date.setDate(date.getDate() + 1);
        return date.toLocaleDateString('es-MX', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).replace(',', ' '); // Remove comma between weekday and rest of date
    };

    const renderEmailView = () => (
        <div className="email-verification-container">
            <h2>Escribe el correo WSP del empleado al que te gustaría consultar.</h2>

            <div className="email-input-container">
                <input
                    type="text"
                    value={email}
                    onChange={handleEmailChange}
                    className="email-input"
                />
            </div>

            <div className="email-example">
                Ejemplo: fernando.nunez@wsp.com.mx
            </div>

            <button
                className="submit-button"
                onClick={handleSubmit}
                disabled={loading}
            >
                {loading ? 'Verificando...' : 'Siguiente'}
            </button>

            {error && (
                <div className="error-message">
                    {error.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                    ))}
                </div>
            )}
        </div>
    );

    // Filter vacation days by the selected year
    const filteredVacationDays = vacationDays.filter(day => {
        const date = new Date(day.vacation_date);
        return date.getFullYear() === selectedYear;
    });

    // Function to handle year selection
    const handleYearSelect = (year) => {
        setSelectedYear(year);
    };

    // Render year selector component
    const renderYearSelector = () => {
        if (availableYears.length <= 1) {
            return (
                <div className="historial-year-selector-container single-year">
                    <div className="historial-current-year">
                        <span>{availableYears[0] || selectedYear}</span>
                    </div>
                </div>
            );
        }
        
        return (
            <div className="historial-year-selector-container">
                <div className="historial-year-selector">
                    {availableYears.map(year => (
                        <button
                            key={year}
                            className={`historial-year-button ${year === selectedYear ? 'active' : ''}`}
                            onClick={() => handleYearSelect(year)}
                        >
                            {year}
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const renderHistoryView = () => (
        <div className="historial-container">
            <h1>Historial de vacaciones: {employee.full_name}</h1>
            <p className="employee-details">
                Correo: {employee.wsp_email} | Días disponibles: {employee.remaining_days} | {
                    overtimeData && overtimeData.days_remaining !== null ? (
                        <span>
                            De tus {employee.remaining_days} días disponibles, <span className="highlight">{overtimeData.days_remaining}</span> días son de prórroga y vencen el <span className="highlight">{formatDate(overtimeData.expiration_date)}</span>
                        </span>
                    ) : (
                        'Todos tus días pertenecen a tu período actual de vacaciones. No tienes días de prórroga'
                    )
                }
            </p>

            {renderYearSelector()}

            <div className="table-container">
                {filteredVacationDays.length === 0 ? (
                    <div className="no-records">
                        {vacationDays.length === 0 
                            ? "No hay registros de vacaciones para este empleado." 
                            : `No hay registros de vacaciones para el año ${selectedYear}.`
                        }
                    </div>
                ) : (
                    <table className="historial-table">
                        <thead>
                            <tr>
                                <th>Fecha</th>
                                <th>Tipo de día</th>
                                {/* Only show Actions column for admin or RH */}
                                {(userRole === 'admin' || userRole === 'RH') && (
                                    <th>Acciones</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVacationDays.map((day) => (
                                <tr key={day.id} className="historial-table-row">
                                    <td>{formatDate(day.vacation_date)}</td>
                                    <td>{day.has_half_day ? 'Medio día' : 'Día completo'}</td>
                                    {/* Only show delete button for admin or RH */}
                                    {(userRole === 'admin' || userRole === 'RH') && (
                                        <td>
                                            <button
                                                className="delete-button"
                                                title="Eliminar"
                                                onClick={() => {
                                                    if (window.confirm(`¿Está seguro que desea eliminar este día de vacaciones?\n\nFecha: ${formatDate(day.vacation_date)}`)) {
                                                        handleDelete(day.id);
                                                    }
                                                }}
                                            >
                                                <TiDelete className="delete-icon" />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Only show the "search another employee" button for non-employee users */}
            {userRole !== 'Empleado' && (
                <button
                    className="search-another-button"
                    onClick={() => {
                        setCurrentView('email');
                        setEmail('@wsp.com.mx');
                        setVacationDays([]);
                        setEmployee(null);
                    }}
                >
                    Buscar otro empleado
                </button>
            )}
        </div>
    );

    return (
        <div className="historial">
            <Navbar />
            <div className="content">
                {loading ? (
                    <div className="loading-container">
                        <p>Cargando información de vacaciones...</p>
                    </div>
                ) : (
                    userRole === 'Empleado' ? 
                        (currentView === 'history' ? renderHistoryView() : renderEmailView()) 
                        : 
                        (currentView === 'email' ? renderEmailView() : renderHistoryView())
                )}
            </div>
        </div>
    );
}

export default Historial;